import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useData } from '../../context/DataContext';
import {
  sessionRegistrationsService,
  usersService,
  homeworksService,
  homeworkSubmissionsService
} from '../../services/firestoreService';
import { Timestamp } from 'firebase/firestore';
import SessionComponentConfig from '../admin/SessionComponentConfig';
import SessionCard from '../shared/SessionCard';
import './CoachSessions.css';

const CoachSessions = () => {
  const { user } = useUser();
  const { sessions: allSessions, courses } = useData();
  const navigate = useNavigate();
  const [mySessions, setMySessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [registrations, setRegistrations] = useState({});
  const [studentsMap, setStudentsMap] = useState({});
  const [homeworks, setHomeworks] = useState({});
  const [showComponentConfig, setShowComponentConfig] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHomeworkSubmissions, setShowHomeworkSubmissions] = useState(null); // sessionId
  const [sessionHomeworks, setSessionHomeworks] = useState({}); // { sessionId: [homeworks] }
  const [homeworkSubmissions, setHomeworkSubmissions] = useState({}); // { homeworkId: [submissions] }

  const normalizeDateValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (value instanceof Date) return value;
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    return null;
  };

  const formatDateDisplay = (value) => {
    const date = normalizeDateValue(value);
    return date ? date.toLocaleDateString() : 'TBD';
  };

  const getSessionStatus = (session) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = normalizeDateValue(session.startDate);
    const endDate = normalizeDateValue(session.endDate);

    if (!startDate || !endDate) {
      return { label: '🔵 Upcoming', type: 'upcoming' };
    }

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (startDate <= today && endDate >= today) {
      return { label: '🟢 Active', type: 'active' };
    } else if (startDate > today) {
      return { label: '🔵 Upcoming', type: 'upcoming' };
    }
    return { label: '⚫ Past', type: 'past' };
  };

  const buildSessionMeta = (session, activeCount) => {
    const meta = [];
    if (session.startDate) {
      meta.push({ icon: '📅', label: 'Start', value: formatDateDisplay(session.startDate) });
    }
    if (session.endDate) {
      meta.push({ icon: '🏁', label: 'End', value: formatDateDisplay(session.endDate) });
    }
    if (session.totalDuration) {
      meta.push({ icon: '⏱️', label: 'Duration', value: `${session.totalDuration} hrs` });
    }
    if (typeof activeCount === 'number') {
      meta.push({ icon: '👥', label: 'Active Students', value: activeCount });
    }
    return meta;
  };

  const buildCourseSection = (session, course) => {
    if (!course && !session.courseObjects && !session.expectations) return null;
    return {
      title: course ? `📚 ${course.title}` : '📚 Course Details',
      description: course?.description,
      objectives: session.courseObjects ? <p>{session.courseObjects}</p> : null,
      expectations: session.expectations ? <p>{session.expectations}</p> : null
    };
  };

  const buildTopicEntries = (session, course) => {
    if (!course?.topics?.length) return [];
    return course.topics.map((topic, idx) => ({
      id: `${session.id}-${idx}`,
      name: topic.name,
      hours: topic.hours ? `${topic.hours}h` : undefined,
      description: topic.description
    }));
  };

  useEffect(() => {
    if (!user || user.type !== 'coach') return;

    // Filter sessions assigned to this coach
    const filtered = allSessions.filter(s => s.coachId === user.id);
    setMySessions(filtered);

    // Load students map
    const loadStudents = async () => {
      const students = await usersService.getAllStudents();
      const map = {};
      students.forEach(s => {
        map[s.id] = s;
      });
      setStudentsMap(map);
    };
    loadStudents();

    // Load registrations for all sessions
    const loadAllRegistrations = async () => {
      const regsMap = {};
      for (const session of filtered) {
        const regs = await sessionRegistrationsService.getSessionRegistrations(session.id);
        regsMap[session.id] = regs;
      }
      setRegistrations(regsMap);
    };
    loadAllRegistrations();

    setLoading(false);
  }, [allSessions, user]);

  useEffect(() => {
    if (!selectedSession) return;

    // Load registrations for selected session
    const loadRegistrations = async () => {
      const regs = await sessionRegistrationsService.getSessionRegistrations(selectedSession.id);
      setRegistrations((prev) => ({ ...prev, [selectedSession.id]: regs }));
    };
    loadRegistrations();

    // Load homeworks for selected session
    const loadHomeworks = async () => {
      const hws = await homeworksService.getHomeworksBySession(selectedSession.id);
      setHomeworks((prev) => ({ ...prev, [selectedSession.id]: hws }));
    };
    loadHomeworks();
  }, [selectedSession]);

  const toggleSessionDetails = (session) => {
    setSelectedSession((prev) => (prev?.id === session.id ? null : session));
  };

  const handleToggleHomework = async (homeworkId, enabled) => {
    try {
      await homeworksService.updateHomework(homeworkId, { enabled });
      // Update local state
      const updatedHomeworks = { ...homeworks };
      if (updatedHomeworks[selectedSession.id]) {
        updatedHomeworks[selectedSession.id] = updatedHomeworks[selectedSession.id].map(hw =>
          hw.id === homeworkId ? { ...hw, enabled } : hw
        );
        setHomeworks(updatedHomeworks);
      }
      alert(`Homework ${enabled ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error('Error toggling homework:', error);
      alert('Error updating homework. Please try again.');
    }
  };

  const handleViewHomeworkSubmissions = async (sessionId) => {
    setShowHomeworkSubmissions(sessionId);
    try {
      // Load homeworks for this session
      const homeworks = await homeworksService.getHomeworksBySession(sessionId);
      setSessionHomeworks(prev => ({ ...prev, [sessionId]: homeworks }));
      
      // Load submissions for each homework
      const submissionsMap = {};
      for (const homework of homeworks) {
        try {
          const subs = await homeworkSubmissionsService.getAllSubmissions(homework.id);
          submissionsMap[homework.id] = subs;
        } catch (error) {
          console.error(`Error loading submissions for homework ${homework.id}:`, error);
          submissionsMap[homework.id] = [];
        }
      }
      setHomeworkSubmissions(prev => ({ ...prev, ...submissionsMap }));
    } catch (error) {
      console.error('Error loading homework submissions:', error);
      alert('Error loading homework submissions. Please try again.');
    }
  };

  const handleAcceptHomework = async (homeworkId, submissionId, rewardPoints) => {
    try {
      // Update submission status to accepted
      await homeworkSubmissionsService.updateSubmission(homeworkId, submissionId, {
        status: 'accepted',
        acceptedAt: Timestamp.now(),
        acceptedBy: user.id
      });
      
      // Award reward points if specified
      if (rewardPoints > 0) {
        const submission = homeworkSubmissions[homeworkId]?.find(s => s.id === submissionId);
        if (submission) {
          // TODO: Award points to student - need to integrate with rewards system
          console.log(`Awarding ${rewardPoints} points to student ${submission.studentId}`);
        }
      }
      
      // Reload submissions
      const subs = await homeworkSubmissionsService.getAllSubmissions(homeworkId);
      setHomeworkSubmissions(prev => ({ ...prev, [homeworkId]: subs }));
      
      alert('✅ Homework accepted successfully!');
    } catch (error) {
      console.error('Error accepting homework:', error);
      alert('Error accepting homework. Please try again.');
    }
  };

  const handleRejectHomework = async (homeworkId, submissionId, feedback) => {
    try {
      await homeworkSubmissionsService.updateSubmission(homeworkId, submissionId, {
        status: 'rejected',
        rejectedAt: Timestamp.now(),
        rejectedBy: user.id,
        feedback: feedback || ''
      });
      
      // Reload submissions
      const subs = await homeworkSubmissionsService.getAllSubmissions(homeworkId);
      setHomeworkSubmissions(prev => ({ ...prev, [homeworkId]: subs }));
      
      alert('Homework rejected.');
    } catch (error) {
      console.error('Error rejecting homework:', error);
      alert('Error rejecting homework. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="coach-sessions">
        <div className="empty-state">
          <p>Loading your sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="coach-sessions">
        <h1>📚 My Sessions</h1>

        {mySessions.length === 0 ? (
          <div className="empty-state">
            <p>No sessions assigned to you yet.</p>
          </div>
        ) : (
          <div className="sessions-list">
            {mySessions.map((session) => {
              const course = session.courseId ? courses.find((c) => c.id === session.courseId) : null;
              const sessionRegs = registrations[session.id] || [];
              const activeRegs = sessionRegs.filter((r) => r.status === 'active');
              const isSelected = selectedSession?.id === session.id;

              const actions = (
                <div className="coach-actions">
                  <button
                    onClick={() => navigate(`/coach/classroom/${session.id}`)}
                    className="join-classroom-btn"
                  >
                    🚀 Open Live Classroom
                  </button>
                  {session.classroomUrl && (
                    <a
                      href={session.classroomUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="join-classroom-btn external-link"
                    >
                      🎥 Join Video Room
                    </a>
                  )}
                  <button
                    onClick={() => {
                      setSelectedSession(session);
                      setShowComponentConfig(true);
                    }}
                    className="config-components-btn"
                  >
                    ⚙️ Configure Components
                  </button>
                  <button
                    onClick={() => toggleSessionDetails(session)}
                    className="view-students-btn"
                  >
                    {isSelected ? '▲ Hide Students' : '▼ View Students'}
                  </button>
                  <button
                    onClick={() => handleViewHomeworkSubmissions(session.id)}
                    className="homework-submissions-btn"
                  >
                    📝 Homework Submissions
                  </button>
                </div>
              );

              return (
                <SessionCard
                  key={session.id}
                  className="coach-session-card"
                  title={session.title}
                  status={getSessionStatus(session)}
                  meta={buildSessionMeta(session, activeRegs.length)}
                  course={buildCourseSection(session, course)}
                  topics={buildTopicEntries(session, course)}
                  actions={actions}
                >
                  {isSelected && (
                    <div className="session-details-panel">
                      <div className="students-section">
                        <h4>Registered Students ({sessionRegs.length})</h4>
                        {sessionRegs.length === 0 ? (
                          <p className="no-data">No students registered yet.</p>
                        ) : (
                          <div className="students-list">
                            {sessionRegs.map((reg) => {
                              const student = studentsMap[reg.studentId];
                              return (
                                <div key={reg.id} className={`student-item ${reg.status}`}>
                                  <div className="student-info">
                                    <strong>{student?.name || 'Unknown Student'}</strong>
                                    <span className={`reg-status ${reg.status}`}>
                                      {reg.status === 'pending' && '⏳ Pending'}
                                      {reg.status === 'active' && '✅ Active'}
                                      {reg.status === 'cancelled' && '❌ Cancelled'}
                                    </span>
                                  </div>
                                  <div className="student-email">{student?.email || ''}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div className="homeworks-section">
                        <h4>Homework Assignments</h4>
                        {homeworks[session.id] && homeworks[session.id].length > 0 ? (
                          <div className="homeworks-list">
                            {homeworks[session.id].map((hw) => (
                              <div key={hw.id} className={`homework-item ${hw.enabled === false ? 'disabled' : ''}`}>
                                <div className="homework-header">
                                  <h5>{hw.title}</h5>
                                  <span className={`homework-status ${hw.enabled === false ? 'disabled' : 'enabled'}`}>
                                    {hw.enabled === false ? '❌ Disabled' : '✅ Enabled'}
                                  </span>
                                </div>
                                {hw.description && (
                                  <p className="homework-desc">{hw.description}</p>
                                )}
                                <div className="homework-due">
                                  Due: {new Date(hw.dueDate).toLocaleString()}
                                </div>
                                <div className="homework-actions">
                                  <button
                                    onClick={() => handleToggleHomework(hw.id, hw.enabled !== false)}
                                    className={hw.enabled === false ? 'enable-btn' : 'disable-btn'}
                                  >
                                    {hw.enabled === false ? '✅ Enable' : '❌ Disable'}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="no-data">No homework assignments for this session.</p>
                        )}
                      </div>
                    </div>
                  )}
                </SessionCard>
              );
            })}
          </div>
        )}
      </div>

      {/* Session Component Configuration Modal */}
      {showComponentConfig && selectedSession && (
        <div className="component-config-modal">
          <div className="modal-content">
            <SessionComponentConfig
              sessionId={selectedSession.id}
              courseId={selectedSession.courseId}
              onSave={() => {
                setShowComponentConfig(false);
                // Optionally refresh data
              }}
              onCancel={() => setShowComponentConfig(false)}
            />
          </div>
        </div>
      )}

      {/* Homework Submissions Modal */}
      {showHomeworkSubmissions && (
        <div className="modal-overlay" onClick={() => setShowHomeworkSubmissions(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>📝 Homework Submissions</h2>
              <button 
                onClick={() => setShowHomeworkSubmissions(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              {sessionHomeworks[showHomeworkSubmissions]?.length > 0 ? (
                <div>
                  {sessionHomeworks[showHomeworkSubmissions].map((homework) => {
                    const submissions = homeworkSubmissions[homework.id] || [];
                    return (
                      <div key={homework.id} style={{ marginBottom: '30px', padding: '20px', border: '2px solid #e2e8f0', borderRadius: '12px' }}>
                        <div style={{ marginBottom: '15px' }}>
                          <h3 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>{homework.title}</h3>
                          {homework.topicName && (
                            <p style={{ margin: '0 0 8px 0', color: '#64748b', fontSize: '0.9rem' }}>
                              <strong>Topic:</strong> {homework.topicName}
                            </p>
                          )}
                          <p style={{ margin: '0 0 8px 0', color: '#64748b', fontSize: '0.9rem' }}>
                            <strong>Due:</strong> {new Date(homework.dueDate).toLocaleString()}
                          </p>
                          {homework.rewardPoints > 0 && (
                            <p style={{ margin: '0 0 8px 0', color: '#f59e0b', fontSize: '0.9rem', fontWeight: '600' }}>
                              ⭐ Reward Points: {homework.rewardPoints}
                            </p>
                          )}
                          {homework.description && (
                            <p style={{ margin: '8px 0', color: '#475569' }}>{homework.description}</p>
                          )}
                        </div>
                        <div>
                          <h4 style={{ margin: '0 0 12px 0', color: '#475569' }}>
                            Submissions ({submissions.length})
                          </h4>
                          {submissions.length > 0 ? (
                            <div>
                              {submissions.map((submission) => {
                                const student = studentsMap[submission.studentId];
                                const isAccepted = submission.status === 'accepted';
                                const isRejected = submission.status === 'rejected';
                                return (
                                  <div 
                                    key={submission.id} 
                                    style={{ 
                                      marginBottom: '15px', 
                                      padding: '15px', 
                                      backgroundColor: isAccepted ? '#f0fdf4' : isRejected ? '#fef2f2' : '#f8fafc',
                                      border: `2px solid ${isAccepted ? '#10b981' : isRejected ? '#ef4444' : '#e2e8f0'}`,
                                      borderRadius: '8px'
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                                      <div>
                                        <strong style={{ display: 'block', marginBottom: '4px' }}>
                                          {student?.name || student?.email?.split('@')[0] || 'Unknown Student'}
                                        </strong>
                                        <small style={{ color: '#64748b' }}>
                                          Submitted: {new Date(submission.submittedAt).toLocaleString()}
                                        </small>
                                      </div>
                                      <span style={{
                                        padding: '4px 12px',
                                        borderRadius: '12px',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        backgroundColor: isAccepted ? '#10b981' : isRejected ? '#ef4444' : '#f59e0b',
                                        color: 'white'
                                      }}>
                                        {isAccepted ? '✅ Accepted' : isRejected ? '❌ Rejected' : '⏳ Pending'}
                                      </span>
                                    </div>
                                    {submission.answer && (
                                      <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: 'white', borderRadius: '6px' }}>
                                        <strong style={{ display: 'block', marginBottom: '6px' }}>Answer:</strong>
                                        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{submission.answer}</p>
                                      </div>
                                    )}
                                    {submission.files && submission.files.length > 0 && (
                                      <div style={{ marginBottom: '10px' }}>
                                        <strong style={{ display: 'block', marginBottom: '6px' }}>Files:</strong>
                                        {submission.files.map((file, idx) => (
                                          <a 
                                            key={idx} 
                                            href={file.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            style={{ 
                                              display: 'inline-block', 
                                              marginRight: '8px', 
                                              padding: '4px 8px', 
                                              backgroundColor: '#e0e7ff', 
                                              borderRadius: '4px',
                                              textDecoration: 'none',
                                              color: '#4f46e5'
                                            }}
                                          >
                                            📎 {file.name}
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                    {submission.feedback && (
                                      <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#fef3c7', borderRadius: '6px' }}>
                                        <strong style={{ display: 'block', marginBottom: '6px' }}>Feedback:</strong>
                                        <p style={{ margin: 0 }}>{submission.feedback}</p>
                                      </div>
                                    )}
                                    {!isAccepted && !isRejected && (
                                      <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                                        <button
                                          onClick={() => handleAcceptHomework(homework.id, submission.id, homework.rewardPoints || 0)}
                                          style={{
                                            padding: '8px 16px',
                                            border: 'none',
                                            borderRadius: '6px',
                                            backgroundColor: '#10b981',
                                            color: 'white',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                          }}
                                        >
                                          ✅ Accept
                                        </button>
                                        <button
                                          onClick={() => {
                                            const feedback = prompt('Enter rejection feedback (optional):');
                                            if (feedback !== null) {
                                              handleRejectHomework(homework.id, submission.id, feedback);
                                            }
                                          }}
                                          style={{
                                            padding: '8px 16px',
                                            border: 'none',
                                            borderRadius: '6px',
                                            backgroundColor: '#ef4444',
                                            color: 'white',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                          }}
                                        >
                                          ❌ Reject
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p style={{ color: '#64748b', fontStyle: 'italic' }}>No submissions yet.</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#64748b', textAlign: 'center', padding: '40px' }}>
                  No homework assignments found for this session.
                </p>
              )}
            </div>
            <div className="form-actions" style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                onClick={() => setShowHomeworkSubmissions(null)} 
                className="cancel-btn"
                style={{ padding: '10px 20px', border: '2px solid #e2e8f0', borderRadius: '8px', background: 'white', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CoachSessions;
